import React, { useState, useEffect } from "react";
import { Release } from "../../types/release.types";
import { releaseApi } from "../../services/release.service";
import { useI18n } from "../../i18n";

const ReleasePage: React.FC = () => {
  const { t, code } = useI18n();
  const [releases, setReleases] = useState<Release[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchReleases = async () => {
      try {
        setLoading(true);
        setError(false);
        const response = await releaseApi.getAllReleases();
        console.log("Releases API response:", response);
        setReleases(response.data || []);
      } catch (err: any) {
        console.error("Error fetching releases:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchReleases();
  }, []);

  const handleDownload = async (releaseId: string) => {
    try {
      const response = await releaseApi.downloadRelease(releaseId);
      console.log("Download response:", response);
      // Open the presigned URL in a new tab
      if (response.data && response.data.downloadUrl) {
        window.open(response.data.downloadUrl, "_blank");
      } else {
        alert(t("releases.errors.noDownloadUrl"));
      }
    } catch (err: any) {
      console.error("Error downloading release:", err);
      alert(t("releases.errors.downloadFailed"));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(code, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="flex flex-col h-full w-full">
      {/* Header (matches deals page) */}
      <header className="flex items-center justify-between h-20 px-8 bg-white border-b border-gray-200 w-full">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{t("releases.title")}</h2>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            {t("releases.subtitle")}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <button className="text-gray-400 hover:text-gray-600">
            <i className="fa-solid fa-bell fa-lg"></i>
          </button>
          {/* Optionally add user avatar here if needed */}
        </div>
      </header>
      {/* Main content */}
      <main className="flex-1 p-8 w-full">
        <section>
          <h3 className="text-xl font-bold text-gray-800 mb-6">{t("releases.sectionTitle")}</h3>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg">
              {t("releases.errors.loadFailed")}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden w-full">
              <div className="divide-y divide-gray-200">
                {releases.length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <i className="fa-solid fa-inbox text-gray-400 text-2xl"></i>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {t("releases.empty.title")}
                      </h3>
                      <p className="text-gray-500 text-sm">
                        {t("releases.empty.body")}
                      </p>
                    </div>
                  </div>
                ) : (
                  releases.map((release) => (
                    <div
                      key={release.id}
                      className="flex justify-between items-center p-4 hover:bg-gray-50/50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <h4 className="text-gray-800 text-sm font-medium">
                            {release.releaseName}
                          </h4>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                            v{release.version}
                          </span>
                          <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                            {release.type}
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          {release.description && (
                            <p className="mb-1">{release.description}</p>
                          )}
                          <p>
                            {t("releases.releasedOn", {
                              date: formatDate(release.createdAt),
                              count: release.downloads,
                            })}
                            {release.fileSize && ` • ${release.fileSize}`}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownload(release.id)}
                        className="text-gray-600 hover:text-gray-900 font-medium text-sm underline cursor-pointer ml-4"
                        disabled={!release.downloadKey}
                      >
                        {t("releases.download")}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default ReleasePage;
